import { Controller } from "@hotwired/stimulus";

// The same five-place, monotonic display used by Longplay's station clock.
// This is a visual clock. Recorded punch times are assigned by the server.
const FRACTION_DIGITS = 5;
const ANCHOR_REFRESH_MS = 60_000;
const FORMAT_STORAGE_KEY = "timekeeping.clock.format";

export default class extends Controller {
  static targets = ["face", "whole", "digit", "date", "motion", "format", "period"];
  static values = { timeZone: { type: String, default: "America/Detroit" } };

  connect() {
    this.isFrozen = false;
    this.hour12 = false;
    try { this.hour12 = window.localStorage?.getItem(FORMAT_STORAGE_KEY) === "12"; } catch (_) { /* Storage may be blocked. */ }
    this.updateFormatState();
    this.dateFormatter = new Intl.DateTimeFormat("en-US", { timeZone: this.timeZoneValue, weekday: "short", month: "short", day: "numeric", year: "numeric" });
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reducedMotion = this.motionQuery.matches;
    this.renderFrame = this.renderFrame.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.motionQuery.addEventListener("change", this.handleMotionChange);
    this.updateMotionState();
    this.syncAnchor();
    this.renderFrame();
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.motionQuery.removeEventListener("change", this.handleMotionChange);
    this.cancelFrame();
  }

  handleVisibilityChange() {
    this.cancelFrame();
    this.setFrozenState(false);
    if (document.hidden) return;
    this.syncAnchor();
    this.renderFrame();
  }

  toggleMotion() {
    this.reducedMotion = !this.reducedMotion;
    this.updateMotionState();
    this.cancelFrame();
    if (!this.isFrozen) this.renderFrame();
  }

  toggleFormat() {
    this.hour12 = !this.hour12;
    try { window.localStorage?.setItem(FORMAT_STORAGE_KEY, this.hour12 ? "12" : "24"); } catch (_) { /* The control still works without storage. */ }
    this.updateFormatState();
    this.renderTime(this.isFrozen ? this.frozenEpochMs : this.currentEpochMilliseconds(), true);
  }

  updateFormatState() {
    this.formatter = new Intl.DateTimeFormat("en-US", { timeZone: this.timeZoneValue, hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: this.hour12 ? "h12" : "h23" });
    this.formatTarget.textContent = this.hour12 ? "🔢" : "🕛";
    const label = this.hour12 ? "Switch to 24-hour time" : "Switch to 12-hour time";
    this.formatTarget.setAttribute("aria-label", label);
    this.formatTarget.setAttribute("title", label);
    this.periodTarget.hidden = !this.hour12;
  }

  handleMotionChange(event) {
    this.reducedMotion = event.matches;
    this.updateMotionState();
    this.cancelFrame();
    if (!this.isFrozen) this.renderFrame();
  }

  updateMotionState() {
    this.element.classList.toggle("is-still", this.reducedMotion);
    this.motionTarget.setAttribute("aria-pressed", String(this.reducedMotion));
    this.motionTarget.textContent = this.reducedMotion ? "▶️" : "⏸️";
    const label = this.reducedMotion ? "Restore clock motion" : "Reduce clock motion";
    this.motionTarget.setAttribute("aria-label", label);
    this.motionTarget.setAttribute("title", label);
  }

  hold(event) {
    const active = event.type === "keydown"
      ? ["Enter", " "].includes(event.key) && !event.repeat
      : event.type === "pointerdown" && event.isPrimary !== false && event.button === 0;
    if (!active || this.isFrozen) return;
    if (event.type === "keydown") event.preventDefault();
    const frozenAt = this.currentEpochMilliseconds();
    this.frozenEpochMs = frozenAt;
    this.setFrozenState(true);
    this.cancelFrame();
    this.renderTime(frozenAt, true);
  }

  release(event) {
    const active = event.type === "keyup" ? ["Enter", " "].includes(event.key) : ["pointerup", "pointercancel", "blur"].includes(event.type);
    if (!active || !this.isFrozen) return;
    if (event.type === "keyup") event.preventDefault();
    this.setFrozenState(false);
    this.syncAnchor();
    this.accessibleSecond = undefined;
    this.renderFrame();
  }

  renderFrame() {
    if (this.isFrozen || document.hidden) return;
    this.renderTime(this.currentEpochMilliseconds());
    if (this.reducedMotion) this.timerRequest = window.setTimeout(this.renderFrame, 1000);
    else this.frameRequest = window.requestAnimationFrame(this.renderFrame);
  }

  currentEpochMilliseconds() {
    let monotonicNow = performance.now();
    if (monotonicNow - this.anchorMonotonicMs >= ANCHOR_REFRESH_MS) {
      this.syncAnchor();
      monotonicNow = performance.now();
    }
    return this.anchorEpochMs + (monotonicNow - this.anchorMonotonicMs);
  }

  renderTime(epochNowMs, forceAccessibleUpdate = false) {
    const epochSecond = Math.floor(epochNowMs / 1000);
    const millisecond = ((epochNowMs % 1000) + 1000) % 1000;
    const fraction = this.reducedMotion ? "00000" : Math.floor(millisecond * (10 ** (FRACTION_DIGITS - 3))).toString().padStart(FRACTION_DIGITS, "0");
    this.digitTargets.forEach((digit, index) => { digit.textContent = fraction[index] || "0"; });
    if (forceAccessibleUpdate || this.accessibleSecond !== epochSecond) {
      const parts = this.formatter.formatToParts(new Date(epochNowMs));
      const whole = ["hour", "minute", "second"].map(type => parts.find(part => part.type === type).value).join(":");
      const period = parts.find(part => part.type === "dayPeriod")?.value || "";
      this.accessibleSecond = epochSecond;
      this.wholeTarget.textContent = whole;
      this.periodTarget.textContent = period;
      this.dateTarget.textContent = this.dateFormatter.format(new Date(epochNowMs));
      this.faceTarget.setAttribute("aria-label", `${this.isFrozen ? "Frozen" : "Current"} time in ${this.timeZoneValue}: ${whole}${period ? ` ${period}` : ""}. ${this.isFrozen ? "Release to return to live time." : "Press and hold to freeze the display."}`);
    }
  }

  syncAnchor() {
    this.anchorEpochMs = Date.now();
    this.anchorMonotonicMs = performance.now();
  }

  cancelFrame() {
    if (this.frameRequest !== undefined) window.cancelAnimationFrame(this.frameRequest);
    if (this.timerRequest !== undefined) window.clearTimeout(this.timerRequest);
    this.frameRequest = undefined;
    this.timerRequest = undefined;
  }

  setFrozenState(frozen) {
    this.isFrozen = frozen;
    if (!frozen) this.frozenEpochMs = undefined;
    this.element.classList.toggle("is-frozen", frozen);
    this.faceTarget.setAttribute("aria-pressed", String(frozen));
  }
}
